import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { FaUser, FaLock, FaSpinner, FaEnvelope, FaPhone, FaMapMarkerAlt, FaGraduationCap, FaBuilding } from 'react-icons/fa';
import { FiUserPlus } from 'react-icons/fi';
import { PasswordStrengthIndicator, validatePassword } from '../utils/passwordValidation';
import { API_BASE_URL } from '../utils/api';
import { motion, AnimatePresence } from 'framer-motion';

export default function Register() {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [phone, setPhone] = useState('');
    const [address, setAddress] = useState('');
    const [grade, setGrade] = useState('');
    const [institution, setInstitution] = useState('');
    const [institutions, setInstitutions] = useState([]);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [emailValidation, setEmailValidation] = useState({ isValid: false, message: '', isChecking: false });
    const [studentType, setStudentType] = useState('physical');
    const { register } = useAuth();
    const navigate = useNavigate();

    const validateEmailFormat = (email) => {
        const emailRegex = /^[^\s@]+@gmail\.com$/i;
        return emailRegex.test(email);
    };

    const checkEmailAvailability = async (emailValue) => {
        if (!emailValue || !validateEmailFormat(emailValue)) {
            setEmailValidation({ isValid: false, message: 'Only Gmail addresses allowed (@gmail.com)', isChecking: false });
            return;
        }

        setEmailValidation({ isValid: false, message: 'Checking...', isChecking: true });

        try {
            const response = await fetch(`${API_BASE_URL}/auth/check_email.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: emailValue })
            });
            const data = await response.json();

            if (data.exists) {
                setEmailValidation({ isValid: false, message: 'Email already registered', isChecking: false });
            } else {
                setEmailValidation({ isValid: true, message: 'Email available', isChecking: false });
            }
        } catch (err) {
            setEmailValidation({ isValid: true, message: 'Valid format', isChecking: false });
        }
    };

    useEffect(() => {
        const fetchInstitutions = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/payment/get_institutions.php`);
                const data = await response.json();
                if (data.status === 'success' && Array.isArray(data.data)) {
                    setInstitutions(data.data);
                }
            } catch (err) {
                console.error('Failed to fetch institutions:', err);
            }
        };
        fetchInstitutions();
    }, []);

    const handleEmailChange = (e) => {
        const emailValue = e.target.value;
        setEmail(emailValue);

        if (window.emailTimeout) clearTimeout(window.emailTimeout);
        window.emailTimeout = setTimeout(() => {
            checkEmailAvailability(emailValue);
        }, 500);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!emailValidation.isValid) {
            return setError('Please enter a valid and available email address');
        }

        if (password !== confirmPassword) {
            return setError('Passwords do not match');
        }

        const validation = validatePassword(password);
        if (!validation.isValid) {
            return setError('Password does not meet minimum requirements');
        }

        try {
            setError('');
            setLoading(true);
            await register(email, password, name, studentType, phone, address, grade, institution);
            navigate('/dashboard');
        } catch (err) {
            setError('Failed to create an account: ' + err);
        } finally {
            setLoading(false);
        }
    };

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 10 },
        visible: { opacity: 1, y: 0 }
    };

    return (
        <div className="min-h-screen relative flex items-center justify-center overflow-hidden bg-[#0a0a0a] font-inter py-12 px-4">
            {/* Animated Background Elements */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <motion.div
                    animate={{
                        x: [0, -100, 0],
                        y: [0, 50, 0],
                        scale: [1, 1.3, 1],
                    }}
                    transition={{
                        duration: 12,
                        repeat: Infinity,
                        ease: "easeInOut"
                    }}
                    className="absolute -top-40 -right-20 w-[600px] h-[600px] bg-blue-600/10 rounded-full blur-[140px]"
                />
                <motion.div
                    animate={{
                        x: [0, 120, 0],
                        y: [0, -80, 0],
                        scale: [1, 1.2, 1],
                    }}
                    transition={{
                        duration: 18,
                        repeat: Infinity,
                        ease: "easeInOut",
                        delay: 2
                    }}
                    className="absolute -bottom-40 -left-20 w-[700px] h-[700px] bg-blue-900/10 rounded-full blur-[140px]"
                />

                {/* Technical Grid */}
                <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_at_center,black,transparent_80%)]"></div>
            </div>

            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                className="relative w-full max-w-lg p-8 md:p-10 bg-white/5 backdrop-blur-2xl rounded-[32px] border border-white/10 shadow-2xl"
            >
                {/* Logo/Icon */}
                <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", stiffness: 200, damping: 15, delay: 0.1 }}
                    className="flex justify-center mb-8"
                >
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-700 rounded-2xl shadow-lg flex items-center justify-center text-white ring-4 ring-white/5">
                        <FiUserPlus size={28} />
                    </div>
                </motion.div>

                {/* Header */}
                <div className="text-center mb-10">
                    <h2 className="text-3xl font-bold text-white tracking-tight mb-3">Create an account</h2>
                    <p className="text-gray-400 text-sm font-medium">
                        Join Maths with Manoj and start learning today.
                    </p>
                </div>

                {/* Error Message */}
                <AnimatePresence>
                    {error && (
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="mb-8 p-4 bg-red-500/10 border border-red-500/20 rounded-2xl text-red-400 text-sm font-medium text-center overflow-hidden"
                        >
                            {error}
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Form */}
                <motion.form
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                    className="grid grid-cols-1 md:grid-cols-2 gap-5"
                    onSubmit={handleSubmit}
                >
                    <motion.div variants={itemVariants} className="space-y-1.5">
                        <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider ml-1">Full Name</label>
                        <div className="relative group">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                <FaUser className="h-4 w-4 text-gray-500 group-focus-within:text-blue-400 transition-colors" />
                            </div>
                            <input
                                id="name"
                                type="text"
                                required
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className="block w-full pl-11 pr-4 py-3.5 bg-white/5 border border-white/10 rounded-2xl text-sm font-medium text-white placeholder-gray-500 focus:outline-none focus:bg-white/10 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300"
                                placeholder="Manoj Perera"
                            />
                        </div>
                    </motion.div>

                    <motion.div variants={itemVariants} className="space-y-1.5">
                        <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider ml-1">Phone (WhatsApp)</label>
                        <div className="relative group">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                <FaPhone className="h-4 w-4 text-gray-500 group-focus-within:text-blue-400 transition-colors" />
                            </div>
                            <input
                                id="phone"
                                type="tel"
                                required
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                                className="block w-full pl-11 pr-4 py-3.5 bg-white/5 border border-white/10 rounded-2xl text-sm font-medium text-white placeholder-gray-500 focus:outline-none focus:bg-white/10 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300"
                                placeholder="07XXXXXXXX"
                            />
                        </div>
                    </motion.div>

                    <motion.div variants={itemVariants} className="md:col-span-2 space-y-2">
                        <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider ml-1">Student Type</label>
                        <div className="grid grid-cols-2 gap-3">
                            <button
                                type="button"
                                onClick={() => setStudentType('physical')}
                                className={`px-4 py-3.5 rounded-2xl text-sm font-bold transition-all duration-300 border flex items-center justify-center gap-2 ${studentType === 'physical'
                                    ? 'bg-blue-600 text-white border-blue-600 shadow-lg shadow-blue-600/20'
                                    : 'bg-white/5 text-gray-400 border-white/5 hover:bg-white/10'
                                    }`}
                            >
                                <span className={`w-2 h-2 rounded-full ${studentType === 'physical' ? 'bg-white animate-pulse' : 'bg-gray-600'}`}></span>
                                Physical Class
                            </button>
                            <button
                                type="button"
                                onClick={() => setStudentType('online')}
                                className={`px-4 py-3.5 rounded-2xl text-sm font-bold transition-all duration-300 border flex items-center justify-center gap-2 ${studentType === 'online'
                                    ? 'bg-blue-600 text-white border-blue-600 shadow-lg shadow-blue-600/20'
                                    : 'bg-white/5 text-gray-400 border-white/5 hover:bg-white/10'
                                    }`}
                            >
                                <span className={`w-2 h-2 rounded-full ${studentType === 'online' ? 'bg-white animate-pulse' : 'bg-gray-600'}`}></span>
                                Online Class
                            </button>
                        </div>
                    </motion.div>

                    <motion.div variants={itemVariants} className="md:col-span-2 space-y-1.5">
                        <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider ml-1">Gmail Address</label>
                        <div className="relative group">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                <FaEnvelope className="h-4 w-4 text-gray-500 group-focus-within:text-blue-400 transition-colors" />
                            </div>
                            <input
                                id="email"
                                type="email"
                                required
                                value={email}
                                onChange={handleEmailChange}
                                className={`block w-full pl-11 pr-12 py-3.5 bg-white/5 border rounded-2xl text-sm font-medium text-white placeholder-gray-500 focus:outline-none focus:bg-white/10 focus:ring-2 transition-all duration-300 ${email && emailValidation.isValid ? 'border-green-500/50 focus:ring-green-500/20' :
                                    email && !emailValidation.isValid && !emailValidation.isChecking ? 'border-red-500/50 focus:ring-red-500/20' :
                                        'border-white/10 focus:ring-blue-500/50 focus:border-blue-500/50'
                                    }`}
                                placeholder="name@gmail.com"
                            />
                            {email && (
                                <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none">
                                    {emailValidation.isChecking ? (
                                        <FaSpinner className="h-4 w-4 text-blue-400 animate-spin" />
                                    ) : emailValidation.isValid ? (
                                        <svg className="h-4 w-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                        </svg>
                                    ) : (
                                        <svg className="h-4 w-4 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                        </svg>
                                    )}
                                </div>
                            )}
                        </div>
                        <AnimatePresence>
                            {email && emailValidation.message && (
                                <motion.div
                                    initial={{ opacity: 0, y: -5 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className={`text-xs font-semibold flex items-center gap-1.5 ml-1 ${emailValidation.isValid ? 'text-green-400' :
                                        emailValidation.isChecking ? 'text-blue-400' : 'text-red-400'
                                        }`}>
                                    <span>{emailValidation.message}</span>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </motion.div>

                    <motion.div variants={itemVariants} className="space-y-1.5">
                        <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider ml-1">Grade</label>
                        <div className="relative group">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                <FaGraduationCap className="h-4 w-4 text-gray-500 group-focus-within:text-blue-400 transition-colors" />
                            </div>
                            <select
                                id="grade"
                                required
                                value={grade}
                                onChange={(e) => setGrade(e.target.value)}
                                className="block w-full pl-11 pr-4 py-3.5 bg-white/5 border border-white/10 rounded-2xl text-sm font-medium text-white placeholder-gray-500 focus:outline-none focus:bg-white/10 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300"
                            >
                                <option value="" disabled className="bg-gray-900">Select Grade</option>
                                {[6, 7, 8, 9, 10, 11].map(g => <option key={g} value={g} className="bg-gray-900">Grade {g}</option>)}
                            </select>
                        </div>
                    </motion.div>

                    <motion.div variants={itemVariants} className="space-y-1.5">
                        <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider ml-1">Institution</label>
                        <div className="relative group">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                <FaBuilding className="h-4 w-4 text-gray-500 group-focus-within:text-blue-400 transition-colors" />
                            </div>
                            <select
                                id="institution"
                                required
                                value={institution}
                                onChange={(e) => setInstitution(e.target.value)}
                                className="block w-full pl-11 pr-4 py-3.5 bg-white/5 border border-white/10 rounded-2xl text-sm font-medium text-white placeholder-gray-500 focus:outline-none focus:bg-white/10 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300"
                            >
                                <option value="" disabled className="bg-gray-900">Select Institution</option>
                                {institutions.map(inst => (
                                    <option key={inst.id} value={inst.name} className="bg-gray-900">{inst.name} - {inst.location}</option>
                                ))}
                            </select>
                        </div>
                    </motion.div>

                    <motion.div variants={itemVariants} className="md:col-span-2 space-y-1.5">
                        <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider ml-1">Address</label>
                        <div className="relative group">
                            <div className="absolute inset-y-0 left-0 pl-4 pt-4 flex items-start pointer-events-none">
                                <FaMapMarkerAlt className="h-4 w-4 text-gray-500 group-focus-within:text-blue-400 transition-colors" />
                            </div>
                            <textarea
                                id="address"
                                required
                                value={address}
                                onChange={(e) => setAddress(e.target.value)}
                                rows="2"
                                className="block w-full pl-11 pr-4 py-3.5 bg-white/5 border border-white/10 rounded-2xl text-sm font-medium text-white placeholder-gray-500 focus:outline-none focus:bg-white/10 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300 resize-none"
                                placeholder="Home Address"
                            />
                        </div>
                    </motion.div>

                    <motion.div variants={itemVariants} className="space-y-1.5">
                        <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider ml-1">Password</label>
                        <div className="relative group">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                <FaLock className="h-4 w-4 text-gray-500 group-focus-within:text-blue-400 transition-colors" />
                            </div>
                            <input
                                id="password"
                                type="password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="block w-full pl-11 pr-4 py-3.5 bg-white/5 border border-white/10 rounded-2xl text-sm font-medium text-white placeholder-gray-500 focus:outline-none focus:bg-white/10 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300"
                                placeholder="••••••••"
                            />
                        </div>
                    </motion.div>

                    <motion.div variants={itemVariants} className="space-y-1.5">
                        <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider ml-1">Confirm</label>
                        <div className="relative group">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                <FaLock className="h-4 w-4 text-gray-500 group-focus-within:text-blue-400 transition-colors" />
                            </div>
                            <input
                                id="confirmPassword"
                                type="password"
                                required
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                className="block w-full pl-11 pr-4 py-3.5 bg-white/5 border border-white/10 rounded-2xl text-sm font-medium text-white placeholder-gray-500 focus:outline-none focus:bg-white/10 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300"
                                placeholder="••••••••"
                            />
                        </div>
                    </motion.div>

                    <motion.div variants={itemVariants} className="md:col-span-2">
                        <PasswordStrengthIndicator password={password} />
                    </motion.div>

                    <motion.div variants={itemVariants} className="md:col-span-2 mt-4">
                        <motion.button
                            whileHover={{ scale: 1.01 }}
                            whileTap={{ scale: 0.98 }}
                            type="submit"
                            disabled={loading}
                            className="w-full h-14 flex items-center justify-center bg-blue-600 hover:bg-blue-500 text-white rounded-2xl font-bold transition-all duration-300 shadow-xl shadow-blue-600/20 disabled:opacity-50 disabled:cursor-not-allowed group"
                        >
                            {loading ? (
                                <FaSpinner className="animate-spin h-5 w-5" />
                            ) : (
                                <span className="flex items-center gap-2">
                                    Create Account
                                    <motion.span
                                        animate={{ x: [0, 4, 0] }}
                                        transition={{ duration: 1.5, repeat: Infinity }}
                                    >
                                        →
                                    </motion.span>
                                </span>
                            )}
                        </motion.button>
                    </motion.div>
                </motion.form>

                {/* Sign In Link */}
                <p className="mt-10 text-center text-sm text-gray-400 font-medium">
                    Already have an account?{' '}
                    <Link to="/login" className="text-white font-bold hover:text-blue-400 transition-colors">
                        Sign in
                    </Link>
                </p>
            </motion.div>
        </div>
    );
}
